import { jwtDecode } from 'jwt-decode';

export interface UserAuth {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  initiate_mfa?: boolean;
}

export function isUserAuth(auth: any): auth is UserAuth {
  return (
    typeof auth?.id_token === 'string' &&
    typeof auth?.access_token === 'string' &&
    typeof auth?.expires_in === 'number' &&
    typeof auth?.token_type === 'string'
    // we deliberately do not include the refresh token
    // no refresh token = you have to log in every 15 minutes
    // also: when refreshing tokens, the response does not include the refresh token
  );
}

export interface PasswordChallenge {
  challenge: string;
}

export interface Options extends RequestInit {
  data?: { [key: string]: any };
  tokens?: UserAuth;
}

async function client(
  endpoint: RequestInfo,
  options: Options = {},
  logOut: () => void
) {
  const config = httpConfig(options);
  const url = isAbsoluteUrl(endpoint)
    ? endpoint
    : `${hostFromId(options.tokens?.id_token)}/${endpoint}`;

  const response = await window.fetch(url, config);

  if (response.status === 401) {
    logOut();
    return Promise.reject({
      message: 'Please re-authenticate.',
      status: response.status,
    });
  }

  let data;
  try {
    data = await response.json();
  } catch (err) {
    data = {};
  }

  if (response.ok) {
    return data;
  }

  return Promise.reject({ status: response.status, ...data });
}

export function isForbidden(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 403;
}

/** Ironman answers 422 for some missing resources, e.g. an ID that isn't an item. */
export function isUnprocessable(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 422;
}

function httpConfig(options: Options): RequestInit {
  return {
    method: options.data ? 'POST' : 'GET',
    body: options.data ? JSON.stringify(options.data) : undefined,
    ...options,
    headers: {
      ...(options.tokens?.access_token
        ? { Authorization: 'Bearer ' + options.tokens?.access_token }
        : undefined),
      ...(options.data ? { 'Content-Type': 'application/json' } : undefined),
      ...options.headers,
    },
  };
}

export function hostFromId(token: string = ''): string {
  try {
    const host = jwtDecode<{ 'custom:bff'?: string }>(token)['custom:bff'];
    if (!host) return '';
    return host.endsWith('/') ? host.slice(0, -1) : host;
  } catch {
    return '';
  }
}

function isAbsoluteUrl(url: RequestInfo) {
  const urlString = url.toString();
  return urlString.includes('http://') || urlString.includes('https://');
}

export default client;
